import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import {
  completarRecienNacido,
  listRecienNacidos,
  type CompletarRecienNacidoPayload,
  type RecienNacidoPendiente,
} from '@lib/services/recien-nacidos.service';
import { filtrarRecienNacidos } from '@lib/filtrar-recien-nacidos';
import { cuilContieneDni } from '@lib/validar-asociado';

const FALTANTE_LABEL: Record<string, string> = {
  documento: 'DNI',
  nombres: 'nombres',
  sexo: 'sexo',
};

type CampoForm = Exclude<keyof CompletarRecienNacidoPayload, 'tipoDoc'>;

/**
 * Campos del formulario. `binary`: viajan a Binary por el trámite de
 * Modificación de datos (identidad por AFI_ID). `portal`: Binary no los acepta
 * por ese circuito (la dirección quedó fuera del alcance y la nacionalidad no
 * es modificable), así que quedan solo en el portal, para el padrón y los
 * reportes. Pedido de ABM del 16/09.
 */
const CAMPOS_FORM: Array<{
  key: CampoForm;
  label: string;
  grupo: 'binary' | 'portal';
  placeholder?: string;
  opciones?: Array<{ value: string; label: string }>;
}> = [
  { key: 'apellSolt', label: 'Apellido', grupo: 'binary' },
  { key: 'nombres', label: 'Nombres', grupo: 'binary' },
  {
    key: 'sexo',
    label: 'Sexo',
    grupo: 'binary',
    opciones: [
      { value: 'M', label: 'M' },
      { value: 'F', label: 'F' },
    ],
  },
  { key: 'fechaNacimiento', label: 'Fecha de nacimiento', grupo: 'binary', placeholder: 'dd/mm/aaaa' },
  { key: 'documento', label: 'DNI', grupo: 'binary', placeholder: 'Sin puntos' },
  { key: 'cuil', label: 'CUIL (si ya lo tiene)', grupo: 'binary', placeholder: '11 dígitos' },
  {
    key: 'estadoCivil',
    label: 'Estado civil',
    grupo: 'binary',
    // Mismo catálogo que el alta (`estado-civil.catalog.ts` del server).
    opciones: [
      { value: 'S', label: 'Soltero' },
      { value: 'C', label: 'Casado' },
      { value: 'P', label: 'Separado/Divorciado' },
    ],
  },
  { key: 'email', label: 'Email', grupo: 'binary' },
  { key: 'areaCelular', label: 'Celular: código de área', grupo: 'binary', placeholder: 'Sin 0' },
  { key: 'numeroCelular', label: 'Celular: número', grupo: 'binary', placeholder: 'Sin 15' },
  {
    key: 'nacionalidad',
    label: 'Nacionalidad',
    grupo: 'portal',
    opciones: [
      { value: 'AR', label: 'Argentina' },
      { value: 'EX', label: 'Extranjera' },
    ],
  },
  { key: 'domicilio', label: 'Calle', grupo: 'portal' },
  { key: 'numero', label: 'Altura', grupo: 'portal' },
  { key: 'piso', label: 'Piso', grupo: 'portal' },
  { key: 'depto', label: 'Departamento', grupo: 'portal' },
  { key: 'cp', label: 'Código postal', grupo: 'portal' },
];

function fmtFecha(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR');
}

function nombreDe(rn: RecienNacidoPendiente): string {
  return `${rn.apellSolt} ${rn.nombres}`.trim() || '—';
}

/**
 * Recién nacidos (VEN-513). Los que Centros Medicus cargó en Binary con datos
 * básicos y el portal sumó al legajo del titular. La organización completa lo
 * que falta; al guardar se genera un trámite de Modificación de datos que
 * viaja a Binary identificado por AFI_ID, así que nunca se duplica la persona.
 */
export function RecienNacidosPage() {
  const qc = useQueryClient();
  const [editando, setEditando] = useState<RecienNacidoPendiente | null>(null);
  const [ultimoTramite, setUltimoTramite] = useState<{
    nombre: string;
    loteId: string | null;
    soloPortal: string[];
  } | null>(null);

  const pendientes = useQuery({ queryKey: ['recien-nacidos'], queryFn: listRecienNacidos });

  const completar = useMutation({
    mutationFn: (vars: { asociadoId: string; nombre: string; payload: CompletarRecienNacidoPayload }) =>
      completarRecienNacido(vars.asociadoId, vars.payload).then((r) => ({ ...r, nombre: vars.nombre })),
    onSuccess: (r) => {
      setUltimoTramite({ nombre: r.nombre, loteId: r.loteId, soloPortal: r.soloPortal ?? [] });
      setEditando(null);
      qc.invalidateQueries({ queryKey: ['recien-nacidos'] });
    },
  });

  // Buscador (VEN-581): filtra en el navegador; la lista viene entera del back.
  const [busqueda, setBusqueda] = useState('');
  const todos = pendientes.data ?? [];
  const items = filtrarRecienNacidos(todos, busqueda);

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">Recién nacidos</h1>
        <p className="text-element-mid">
          Recién nacidos que Centros Medicus cargó en Binary con datos básicos y que el portal sumó al
          legajo del titular. Completá lo que falta: los datos van a Binary sobre la misma persona, sin
          duplicarla, y el trámite queda en Modificación de datos.
        </p>
      </header>

      {ultimoTramite && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <span className="status status-success">{ultimoTramite.loteId ? 'enviado' : 'guardado'}</span>{' '}
          {ultimoTramite.loteId ? (
            <>
              Los datos de <strong>{ultimoTramite.nombre}</strong> salieron hacia Binary. Seguí el trámite en{' '}
              <Link to="/modificaciones">Modificación de datos</Link>.
            </>
          ) : (
            <>
              Los datos de <strong>{ultimoTramite.nombre}</strong> quedaron guardados en el portal.
            </>
          )}
          {ultimoTramite.soloPortal.length > 0 && ultimoTramite.loteId && (
            <span className="text-element-mid"> Nacionalidad, domicilio o CP quedaron solo en el portal.</span>
          )}
          <button className="btn btn-link" style={{ marginLeft: 8 }} onClick={() => setUltimoTramite(null)}>
            Cerrar
          </button>
        </div>
      )}

      <div className="card" style={{ marginTop: '1rem' }}>
        <div className="rowBetween" style={{ alignItems: 'center', marginBottom: 8 }}>
          <h2 className="heading-5">Pendientes de completar</h2>
          <span className="text-element-mid" style={{ fontSize: 13 }}>
            {busqueda.trim() && items.length !== todos.length ? `${items.length} de ${todos.length}` : items.length}{' '}
            pendiente{todos.length === 1 ? '' : 's'}
          </span>
        </div>
        {todos.length > 0 && (
          <input
            type="search"
            className="input"
            placeholder="Buscar por DNI, legajo o apellido/nombre del titular o del recién nacido…"
            aria-label="Buscar recién nacido"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ marginBottom: 12, width: '100%', maxWidth: 560 }}
          />
        )}
        {pendientes.isLoading && <TableSkeleton rows={4} />}
        {pendientes.isError && <p className="status status-error">error al cargar la lista.</p>}
        {pendientes.data && todos.length === 0 && (
          <p className="text-element-mid">No hay recién nacidos pendientes de completar.</p>
        )}
        {todos.length > 0 && items.length === 0 && (
          <p className="text-element-mid">
            Ningún recién nacido coincide con «{busqueda.trim()}».{' '}
            <button type="button" className="btn btn-link" onClick={() => setBusqueda('')}>
              Limpiar búsqueda
            </button>
          </p>
        )}
        {items.length > 0 && (
          <div className="tableWrap">
            <table className="dataTable">
              <thead>
                <tr>
                  <th>Recién nacido</th>
                  <th>Nacimiento</th>
                  <th>DNI</th>
                  <th>Sexo</th>
                  <th>Titular</th>
                  <th>Legajo</th>
                  <th>Falta</th>
                  <th>Detectado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((rn) => (
                  <tr key={rn.asociadoId}>
                    <td>
                      <strong>{nombreDe(rn)}</strong>
                      <div className="text-element-mid" style={{ fontSize: 12 }}>
                        AFI {rn.binaryAfiId}
                      </div>
                    </td>
                    <td>{rn.fechaNacimiento || '—'}</td>
                    <td>{rn.documento || <span className="text-element-mid">sin DNI</span>}</td>
                    <td>{rn.sexo || '—'}</td>
                    <td>
                      {rn.titular ? (
                        <>
                          {rn.titular.apellSolt} {rn.titular.nombres}
                          <div className="text-element-mid" style={{ fontSize: 12 }}>
                            DNI {rn.titular.documento}
                          </div>
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{rn.legajo}</td>
                    <td>
                      {rn.camposFaltantes.length === 0 ? (
                        <span className="text-element-mid">revisar</span>
                      ) : (
                        rn.camposFaltantes.map((c) => (
                          <span key={c} className="status status-warning" style={{ marginRight: 4 }}>
                            {FALTANTE_LABEL[c] ?? c}
                          </span>
                        ))
                      )}
                    </td>
                    <td>{fmtFecha(rn.detectadoAt)}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditando(rn)}>
                        Completar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editando && (
        <CompletarModal
          rn={editando}
          saving={completar.isPending}
          error={
            completar.isError
              ? // El server responde `{ error: { code, message } }` (HttpExceptionFilter):
                // leer `data.message` escondía siempre el motivo real.
                ((completar.error as { response?: { data?: { error?: { message?: string } } } })
                  .response?.data?.error?.message ?? 'No se pudo enviar el trámite')
              : null
          }
          onCerrar={() => {
            setEditando(null);
            completar.reset();
          }}
          onGuardar={(payload) =>
            completar.mutate({ asociadoId: editando.asociadoId, nombre: nombreDe(editando), payload })
          }
        />
      )}
    </section>
  );
}

/**
 * Formulario de completar. Se manda solo lo que cambia respecto de lo que el
 * portal ya tiene; el backend descarta lo igual y valida la forma de cada
 * valor con las mismas reglas que el archivo de Modificación de datos.
 */
export function CompletarModal({
  rn,
  saving,
  error,
  onCerrar,
  onGuardar,
}: {
  rn: RecienNacidoPendiente;
  saving: boolean;
  error: string | null;
  onCerrar: () => void;
  onGuardar: (payload: CompletarRecienNacidoPayload) => void;
}) {
  // Valor inicial: lo que el portal ya tiene. "BEBE" no es un nombre: arranca vacío.
  const inicial: Record<CampoForm, string> = {
    apellSolt: rn.apellSolt,
    nombres: /\bBEBE\b/i.test(rn.nombres) ? '' : rn.nombres,
    sexo: rn.sexo,
    fechaNacimiento: rn.fechaNacimiento,
    documento: rn.documento,
    cuil: rn.cuil,
    estadoCivil: rn.estadoCivil,
    email: rn.email,
    areaCelular: rn.areaCelular,
    numeroCelular: rn.numeroCelular,
    nacionalidad: rn.nacionalidad,
    domicilio: rn.domicilio,
    numero: rn.numero,
    piso: rn.piso,
    depto: rn.depto,
    cp: rn.cp,
  };
  const [valores, setValores] = useState<Record<CampoForm, string>>(inicial);
  const set = (k: CampoForm, v: string) => setValores((prev) => ({ ...prev, [k]: v }));

  // Solo lo que cambió respecto de lo guardado (el backend vuelve a filtrar igual).
  const payload: CompletarRecienNacidoPayload = {};
  for (const c of CAMPOS_FORM) {
    const nuevo = valores[c.key].trim();
    const actual = (c.key === 'nombres' ? rn.nombres : inicial[c.key]).trim();
    if (nuevo && nuevo !== actual) payload[c.key] = nuevo;
  }
  if (payload.documento) payload.tipoDoc = rn.tipoDoc || 'DNI';
  const hayCambios = Object.keys(payload).length > 0;

  // Pedido de MS (VEN-637): si el CUIL no contiene al DNI no se avanza. Se
  // cruzan los valores finales (lo tipeado o, si no, lo que el portal ya
  // tiene); el backend vuelve a chequearlo igual.
  const documentoFinal = valores.documento.trim();
  const cuilFinal = valores.cuil.trim();
  const errorCuil =
    cuilFinal && !cuilContieneDni(cuilFinal, documentoFinal, rn.tipoDoc || 'DNI')
      ? `El CUIL ${cuilFinal} no contiene al DNI ${documentoFinal}. Revisá los dos antes de enviar.`
      : null;

  const campo = (c: (typeof CAMPOS_FORM)[number]) => (
    <label className="formField" key={c.key}>
      <span>{c.label}</span>
      {c.opciones ? (
        <select className="input" value={valores[c.key]} onChange={(e) => set(c.key, e.target.value)}>
          <option value="">—</option>
          {c.opciones.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="input"
          value={valores[c.key]}
          placeholder={c.key === 'nombres' && /\bBEBE\b/i.test(rn.nombres) ? 'Binary lo tiene como "BEBE"' : c.placeholder}
          onChange={(e) => set(c.key, e.target.value)}
        />
      )}
    </label>
  );

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true" aria-label={`Completar ${nombreDe(rn)}`} onClick={onCerrar}>
      <div className="modalCard" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <h3 className="heading-5">Completar recién nacido</h3>
        <p className="text-element-mid" style={{ fontSize: 13 }}>
          Legajo {rn.legajo}
          {rn.titular ? ` · titular ${rn.titular.apellSolt} ${rn.titular.nombres}` : ''} · AFI {rn.binaryAfiId}
        </p>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-element-mid)', marginTop: 12 }}>
          Datos que viajan a Binary
        </div>
        <div className="modalGrid" style={{ marginTop: 6 }}>
          {CAMPOS_FORM.filter((c) => c.grupo === 'binary').map(campo)}
        </div>
        {errorCuil && (
          <p className="status status-error" style={{ marginTop: 8 }} role="alert">
            {errorCuil}
          </p>
        )}
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-element-mid)', marginTop: 12 }}>
          Datos solo del portal
          <span className="text-element-mid" style={{ fontWeight: 400 }}>
            {' '}
            · Binary no los recibe por este circuito; quedan para el padrón y los reportes.
          </span>
        </div>
        <div className="modalGrid" style={{ marginTop: 6 }}>
          {CAMPOS_FORM.filter((c) => c.grupo === 'portal').map(campo)}
        </div>
        {error && (
          <p className="status status-error" style={{ marginTop: 8 }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onCerrar} disabled={saving}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            disabled={!hayCambios || saving || Boolean(errorCuil)}
            onClick={() => onGuardar(payload)}
          >
            {saving ? 'Enviando…' : 'Enviar a Binary'}
          </button>
        </div>
      </div>
    </div>
  );
}
